import { computed } from '@angular/core';
import {
    deepComputed,
    patchState,
    signalStore,
    watchState,
    withComputed,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    addEntities,
    addEntity,
    entityConfig,
    EntityId,
    removeAllEntities,
    removeEntity,
    setAllEntities,
    updateEntity,
    withEntities,
} from '@ngrx/signals/entities';
import { VesselSchedule } from '@pmis3/vessel-activity-core';
import {
    Position,
    PositionBase,
    TimelineDate,
} from '../planner/planner.component';
import { withUndoRedo } from './feature/undo-redo.feature';
import { type } from '@ngrx/signals';
import { EntityChanges } from '@ngrx/signals/entities/src/models';
import { ContentScrollEvent } from '@progress/kendo-angular-grid';
import { isEquals } from '../planner/planner-helpers/planner-util';
import { AppUtils, DateForPmis } from '@pmis3/utils';

//could keep track of changes
//removed[]
//added[]
//updated[] updated would have the new value and the original value, and the property that was updated
// if the new value + old value are the same then get rid of that property.
// but updated might still need to be kept if there are other properties
// updated might just keep the whole obj or just the id.
export type PlannerMode = 'view' | 'edit' | 'all';
const timeIntervalConfig = [
    { maxDays: 14, minDays: 11, interval: 8 }, // 8 hours for 11-14 days
    { maxDays: 10, minDays: 8, interval: 6 }, // 6 hours for 8-10 days
    { maxDays: 7, minDays: 4, interval: 4 }, // 4 hours for 4-7 days
    { maxDays: 3, minDays: 2, interval: 2 }, // 2 hours for 2-3 days
    { maxDays: 1, minDays: 1, interval: 1 }, // 1 hour for 1 day
];

type EventsState = {
    isLoading: boolean;
    _initialEvents: VesselSchedule[];
    _initialPositions: Position[];
    _initialEventsIds: string[];
    _initialPositionsIds: string[];
    settings: {
        zoomLevel: number;
        zoomRatio: number;
        mode: PlannerMode;
        pendingAssignment: boolean;
        fitToScreen: boolean;
        showClearance: boolean;
        daysShown: number;
        timeIntervalConfig: {
            maxDays: number;
            minDays: number;
            interval: number;
        }[];
        distanceUnit: 'm' | 'ft';

        // timeInterval: number;
    };
    scrollingEvent: ContentScrollEvent | null;
    dirty: boolean;
    currentTime: {
        time: Date;
        position: PositionBase;
    };
    // timeline: {
    //     timeIntervalConfig: { maxDays: number, minDays: number, interval: number; }[];
    //     startDate: Date;
    //     daysShown: number;
    //     // timeInterval: number;
    //     timelineDates: TimelineDate[];

    // },
    startDate: Date;
    endDate: Date;
    visibleEvents: Map<string, boolean>;
    exportSettings: {
        startDate?: DateForPmis;
        endDate?: DateForPmis;
    };
};

const initialState: EventsState = {
    isLoading: false,
    _initialEvents: [],
    _initialPositions: [],
    _initialEventsIds: [],
    _initialPositionsIds: [],
    settings: {
        zoomLevel: 1,
        zoomRatio: 0,
        mode: 'view',
        pendingAssignment: true,
        fitToScreen: false,
        showClearance: false,
        daysShown: 14,
        timeIntervalConfig: timeIntervalConfig,
        distanceUnit: 'm',

        // daysShown: 14,
        // timeInterval: 8
    },
    scrollingEvent: null,
    dirty: false,
    currentTime: {
        time: new Date(),
        position: {
            id: '',
            xStart: 0,
            xEnd: 0,
            yStart: 0,
            yEnd: 0,
            length: 0,
        },
    },
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(
        new Date().setHours(0, 0, 0, 0) + 28 * 24 * 60 * 60 * 1000
    ),
    visibleEvents: new Map<string, boolean>(),
    exportSettings: {
        startDate: undefined,
        endDate: undefined,
    },
};

function deepCloneState(events: VesselSchedule[]): VesselSchedule[] {
    return events.map((event) => {
        return event?.copy();
    });
}

function deepCloneState2(events: Position[]): Position[] {
    return events.map((event) => {
        return { ...event };
    });
}

const eventsConfig = entityConfig({
    entity: type<VesselSchedule>(),
    collection: 'events',
});

const positionConfig = entityConfig({
    entity: type<Position>(),
    collection: 'positions',
});

export const EventStore = signalStore(
    withEntities(eventsConfig),
    withEntities(positionConfig),
    withUndoRedo({
        maxStackSize: 100,
        collections: ['events'],
        skip: 0,
    }),
    withState(initialState),
    withComputed(
        ({
            _initialEvents,
            _initialPositions,
            positionsIds,
            eventsEntities,
            positionsEntities,
            eventsIds,
            _initialPositionsIds,
            eventsEntityMap,
            settings,
            startDate,
            endDate,
            exportSettings,
        }) => {
            const canRevert = computed(
                () =>
                    _initialEvents !== undefined &&
                    _initialEvents() !== undefined
            );
            const assigned = computed(() =>
                eventsEntities().filter((x) => positionsIds().includes(x.id))
            );
            const assignedIds = computed(() =>
                eventsIds().filter((x) => positionsIds().includes(x))
            );
            const pending = computed(() =>
                eventsEntities().filter((x) => !positionsIds().includes(x.id))
            );
            const pendingIds = computed(() =>
                eventsIds().filter((x) => !positionsIds().includes(x))
            );
            // _initialPositionIds: computed(() => initialPositions().map(x => x.id)),
            const changes = deepComputed(() => ({
                removed: _initialPositionsIds().filter(
                    (x) => !positionsIds().includes(x as string)
                ),
                added: positionsIds().filter(
                    (x) => !_initialPositionsIds().includes(x as string)
                ) as string[],
                changed: positionsEntities()
                    .filter((x) => {
                        if (!_initialPositionsIds().includes(x.id))
                            return false;
                        return (
                            !isEquals(
                                x,
                                _initialPositions().find((p) => p.id == x.id)
                            ) ||
                            !isEquals(
                                eventsEntityMap()[x.id],
                                _initialEvents().find((p) => p.id == x.id)
                            )
                        );
                    })
                    .map((x) => x.id),
            }));

            const zoomLevelOrRatio = computed(() => {
                const setting = settings();
                const { zoomLevel, zoomRatio, fitToScreen } = setting;
                return (!zoomRatio || zoomRatio <= zoomLevel) && !fitToScreen
                    ? zoomLevel
                    : zoomRatio;
            });

            const timeInterval = computed(() => {
                const daysShown = settings.daysShown();
                const config = settings
                    .timeIntervalConfig()
                    .find(
                        (cfg) =>
                            daysShown >= cfg.minDays && daysShown <= cfg.maxDays
                    );
                return config?.interval ?? 1;
            });

            const timeline = computed(() => {
                const { startDate: exportStartDate, endDate: exportEndDate } =
                    exportSettings();
                const start = exportStartDate ?? startDate();
                const end = exportEndDate ?? endDate();

                return AppUtils.DateTime.CreateTimeline(
                    start,
                    end,
                    `${timeInterval()}hh`
                );
            });

            return {
                canRevert,
                assigned,
                assignedIds,
                pending,
                pendingIds,
                changes,
                zoomLevelOrRatio,
                timeInterval,
                timeline,
            };
        }
    ),
    withMethods((store) => ({
        loadInitialEvents(
            e: { event: VesselSchedule; position?: Position }[]
        ): void {
            const events = e.map((x) => x.event);
            const positions = e.map((x) => x.position).filter((x) => !!x);
            patchState(store, {
                isLoading: false,
                _initialEvents: deepCloneState(events),
                _initialPositions: deepCloneState2(positions),
                _initialPositionsIds: positions.map((x) => x.id),
                _initialEventsIds: events.map((x) => x.id),
                settings: { ...store.settings(), mode: 'view' },
            });
            this.addEventsAndPositions(events, positions);
            this.setDirty(false);
            setTimeout(() => store.clearStack());
        },
        addEvent(event: VesselSchedule): void {
            patchState(store, addEntity(event, eventsConfig));
        },
        addEvents(events: VesselSchedule[]): void {
            patchState(store, addEntities(events, eventsConfig));
        },
        addPosition(pos: Position): void {
            this.setDirty(true);
            patchState(store, addEntity(pos, positionConfig));
        },
        addPositions(poss: Position[]): void {
            patchState(store, addEntities(poss, positionConfig));
        },
        addEventAndPosition(event: VesselSchedule, pos: Position): void {
            this.addEvent(event);
            this.addPosition(pos);
        },
        addEventsAndPositions(
            events: VesselSchedule[],
            poss: Position[]
        ): void {
            this.addEvents(events);
            this.addPositions(poss);
        },
        // removeEvent(eventId: string): void {
        //     patchState(store, removeEntity(eventId, eventsConfig));
        //     patchState(store, removeEntity(eventId, positionConfig));
        // },
        removePosition(eventId: string): void {
            this.setDirty(true);
            patchState(store, removeEntity(eventId, positionConfig));
        },
        removeAllEvents(): void {
            patchState(store, removeAllEntities(eventsConfig));
            // store.positionsEntities().forEach((position) => {
            //     position?.component?.destroy();
            // });
            patchState(store, removeAllEntities(positionConfig));
            store.clearStack();
        },
        updateEvent(
            id: EntityId,
            changes: EntityChanges<VesselSchedule>
        ): void {
            this.setDirty(true);
            patchState(
                store,
                updateEntity({ id: id, changes: changes }, eventsConfig)
            );
        },
        updatePosition(id: EntityId, changes: EntityChanges<Position>): void {
            this.setDirty(true);
            patchState(
                store,
                updateEntity({ id: id, changes: changes }, positionConfig)
            );
        },
        revert() {
            if (store.canRevert() && store._initialEvents !== undefined) {
                this.setDirty(false);
                const initialEvents = store._initialEvents();
                const initialPositions = store._initialPositions();
                patchState(store, setAllEntities(initialEvents, eventsConfig));
                // store.positionsEntities().forEach((position) => {
                // const iPos = initialPositions.find(p => p.id == position.id);
                //should destroy all the components and rerender, this makes it easier to handle reverting the initial state of the components? or find a way to store the state of the component inside the component? and revert everything,
                // could use a computed signal to store the state of the component when its !edit then revert
                // if(!iPos && position.component)
                //     position.component.destroy();
                // });
                patchState(
                    store,
                    setAllEntities(initialPositions, positionConfig)
                );
            }
        },
        setZoomLevel(zoomLevel: number) {
            const settings = store.settings();
            patchState(store, {
                settings: {
                    ...settings,
                    zoomLevel: zoomLevel,
                    fitToScreen: false,
                },
            });
        },
        setZoomRatio(zoomRatio: number) {
            patchState(store, {
                settings: { ...store.settings(), zoomRatio: zoomRatio },
            });
        },
        setMode(mode: 'view' | 'edit') {
            patchState(store, {
                settings: { ...store.settings(), mode: mode },
            });
        },
        setDirty(dirty: boolean) {
            patchState(store, { dirty: dirty });
        },
        setPendingAssignment(isCollapsed: boolean) {
            patchState(store, {
                settings: {
                    ...store.settings(),
                    pendingAssignment: isCollapsed,
                },
            });
        },
        setFitToScreen(fitToScreen: boolean) {
            patchState(store, {
                settings: { ...store.settings(), fitToScreen: fitToScreen },
            });
        },
        setDisplayClearance(showClearance: boolean) {
            patchState(store, {
                settings: { ...store.settings(), showClearance: showClearance },
            });
        },
        setDaysShown(days: number) {
            patchState(store, {
                settings: { ...store.settings(), daysShown: days },
            });
        },
        setVisibleEvent(eventId: string, isVisible: boolean) {
            const visibleEvents = new Map(store.visibleEvents());
            visibleEvents.set(eventId, isVisible);
            patchState(store, { visibleEvents: visibleEvents });
        },
        setDate(startDate: Date, endDate: Date) {
            patchState(store, {
                startDate: new Date(startDate),
                endDate: new Date(endDate),
            });
        },
        setExportSettings(startDate?: Date, endDate?: Date) {
            patchState(store, {
                exportSettings: {
                    startDate: startDate
                        ? new DateForPmis(startDate)
                        : undefined,
                    endDate: endDate ? new DateForPmis(endDate) : undefined,
                },
            });
        },
        setDistanceUnit(unit: 'm' | 'ft') {
            patchState(store, {
                settings: { ...store.settings(), distanceUnit: unit },
            });
        },
        undo() {
            console.log('help');
            if (!store.canUndo()) return;
            store.undo();
        },
        updateScrollingEvent(event: any) {
            patchState(store, { scrollingEvent: event });
        },
        updateCurrentTime(time: Date, position: PositionBase) {
            patchState(store, {
                currentTime: { time: time, position: position },
            });
        },
    }))
);
